import React, { useEffect } from 'react';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import logo from '/lovable-uploads/40a4ee59-7714-4091-a6f7-e5ba5819fe5f.png';
import { useOnboardingFlow } from '@/hooks/useOnboardingFlow';

// Import the smaller components
import OnboardingProgressBar from '@/components/onboarding/OnboardingProgressBar';
import InstallAppStep from '@/components/onboarding/InstallAppStep';
import InfoStep from '@/components/onboarding/InfoStep';
import EmailStep from '@/components/onboarding/EmailStep';
import VerificationStep from '@/components/onboarding/VerificationStep';
import SuccessStep from '@/components/onboarding/SuccessStep';

// Set to false to disable auto-filling OTP codes
const AUTO_FILL_OTP = false;

const Onboarding = () => {
  // Clear cache conditionally when the onboarding component mounts
  useEffect(() => {
    // Only clear cache if user is not already verified/completed
    const userData = localStorage.getItem('userData');
    let shouldClearCache = true;
    
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        // Don't clear cache if user has completed onboarding
        if (parsed.onboardingCompleted === true) {
          console.log('User already completed onboarding, preserving cache');
          shouldClearCache = false;
        }
      } catch (error) {
        console.error('Error parsing user data, will clear cache:', error);
      }
    }

    if (shouldClearCache) {
      // Clear browser cache only if needed
      const clearCache = async () => {
        if ('caches' in window) {
          try {
            const cacheNames = await window.caches.keys();
            await Promise.all(
              cacheNames.map(cacheName => {
                console.log('Clearing cache:', cacheName);
                return window.caches.delete(cacheName);
              })
            );
            console.log('Cache cleared successfully');
          } catch (error) {
            console.error('Error clearing cache:', error);
          }
        }
      };

      clearCache();
    }
    
    // Only clear OTP data if we're starting fresh (not in verification step)
    const currentStep = localStorage.getItem('currentOnboardingStep');
    if (currentStep !== 'verification') {
      localStorage.removeItem('userOtpCode');
      localStorage.removeItem('userOtpExpiration');
      console.log('Cleared OTP data for fresh start');
    } else {
      console.log('Preserving OTP data for verification step');
    }
    
    // Always clear phone verification flag (temporary data)
    localStorage.removeItem('verifyingPhone');
  }, []);

  const {
    step,
    name,
    setName,
    email,
    setEmail,
    emailError,
    resendDisabled,
    countdown,
    isLoading,
    verifyingPhone,
    handleInfoContinue,
    handleEmailContinue,
    handleOtpComplete,
    handleResendOtp,
    goToInfoStep,
    goToEmailStep,
    handleInstallAppContinue,
    handleInstallAppSkip
  } = useOnboardingFlow();

  const renderStepContent = () => {
    switch (step) {
      case 'install':
        return (
          <InstallAppStep 
            onContinue={handleInstallAppContinue}
          />
        );
      
      case 'info':
        return (
          <InfoStep 
            name={name} 
            setName={setName} 
            onContinue={handleInfoContinue} 
          />
        );
      
      case 'email':
        return (
          <EmailStep 
            email={email}
            setEmail={setEmail}
            emailError={emailError}
            onContinue={handleEmailContinue}
            onBack={goToInfoStep}
            isLoading={isLoading}
          />
        );
      
      case 'verification':
        return (
          <VerificationStep 
            email={email}
            resendDisabled={resendDisabled}
            countdown={countdown}
            onOtpComplete={handleOtpComplete}
            onResendOtp={handleResendOtp}
            onBack={goToEmailStep}
            autoFillOtp={AUTO_FILL_OTP}
            verifyingPhone={false}
          />
        );
      
      case 'success':
        return <SuccessStep />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-purple-50 to-pink-50">
      <NavBar 
        title="Configuration de compte" 
        logo={logo} 
        colorScheme="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600" 
      />
      
      <main className="container mx-auto px-4 py-6 pb-20 flex flex-col items-center">
        <OnboardingProgressBar step={step} />
        {renderStepContent()}
      </main>
      
      <Footer />
    </div>
  );
};

export default Onboarding;
